/**
 * Which Microsoft account Procura signs in as, decided once at start-up
 * (Arthur, 19 October 2026). A leaf module with no imports, so it is tested
 * without MSAL (web/test/account-choice.test.ts).
 *
 * WHY. Until then, with no redirect just completed, session.tsx made the FIRST
 * account in MSAL's cache active (getAllAccounts()[0]). A browser that holds
 * two Microsoft accounts, as Arthur's held arthur.fain@ and sys@, was signed
 * into Procura as whichever MSAL listed first: an order nobody chose. On 19
 * October Procura moved to sys@ without anyone choosing it.
 *
 * THE RULE.
 *   - An account a person has just chosen (a redirect completed) is used, and
 *     REMEMBERED: its homeAccountId, in this browser's localStorage.
 *   - Otherwise the remembered account is used, if MSAL still holds it.
 *   - Otherwise, if MSAL holds any account at all, Microsoft's account picker
 *     (prompt=select_account): never a silent choice among them.
 *   - With no account cached, the sign-in screen, whose button also opens the
 *     picker.
 */

/** This browser's remembered account: an MSAL homeAccountId. */
export const REMEMBERED_ACCOUNT = 'procura.account';

export interface AccountLike {
  homeAccountId: string;
}

export type AccountChoice<A extends AccountLike> =
  | { kind: 'use'; account: A; remember: boolean }
  | { kind: 'pick' }
  | { kind: 'sign-in' };

export function chooseAccount<A extends AccountLike>(
  justChosen: A | null,
  cached: readonly A[],
  remembered: string | null,
): AccountChoice<A> {
  if (justChosen) return { kind: 'use', account: justChosen, remember: true };
  if (remembered) {
    const known = cached.find((a) => a.homeAccountId === remembered);
    if (known) return { kind: 'use', account: known, remember: false };
  }
  return cached.length > 0 ? { kind: 'pick' } : { kind: 'sign-in' };
}
